import { cssToPhaser, Theme } from "../config/theme.js";

// Skill bar colour per element — used while charging and at ready state
const ELEM_BAR_COLOR = {
  fire:    { charge: 0xef4444, hex: '#ef4444' },  // red
  electric:{ charge: 0xfacc15, hex: '#facc15' },  // yellow
  water:   { charge: 0x38bdf8, hex: '#38bdf8' },  // sky blue
  earth:   { charge: 0x4ade80, hex: '#4ade80' },  // green
  psychic: { charge: 0xc084fc, hex: '#c084fc' },  // purple
};
const READY_COLOR  = 0xfcd34d; // gold — same for all when skill ready

// ── Skill-ready personality quips ────────────────────────────────────────────
const READY_QUIPS = {
  terra:    ["A natureza responde.", "O poder da terra… agora.", "Estou pronta."],
  fogo:     ["Não brinques com o fogo.", "Hora de queimar.", "Acaba com isto."],
  raio:     ["Usa agora. Estás à espera de quê?", "Carregado. Dispara!", "Finalmente!"],
  jato:     ["Sente o fluxo.", "Está alinhado.", "Agora…"],
  controlo: ["Sem erro.", "Momento ideal.", "Executa."],
};

export class MonsterBar {
  constructor(scene, monsters, { onUse } = {}) {
    this.scene = scene;
    this.onUse = onUse || (() => {});
    this.monsters = monsters.map((m) => ({ ...m, energy: 0, ready: false }));
    this.activeIndex = 0;

    const h = 200;
    this.barY = scene.scale.height - h / 2;

    this.bg = scene.add
      .rectangle(scene.scale.width / 2, this.barY, scene.scale.width, h, 0x0f172a, 0.22)
      .setDepth(50);

    this.items = [];
    this._xpData = {}; // { monsterId: { xp, level } } — preview durante o nível
    this.draw();
    this.setActive(0);
  }

  draw() {
    const w = this.scene.scale.width;
    const pad = 10;
    const itemW = (w - pad * 2) / this.monsters.length;
    const topY = this.barY - 40;

    for (let i = 0; i < this.monsters.length; i++) {
      const cx = pad + itemW * i + itemW / 2;

      const card = this.scene.add
        .rectangle(cx, topY, itemW - 10, 112, 0x111827, 0.85)
        .setStrokeStyle(2, 0x334155, 1)
        .setDepth(51)
        .setInteractive();

      // -- Zone 1: sprite --
      const icon = this.scene.add.image(cx, topY - 40, `monster-${this.monsters[i].id}`)
        .setDisplaySize(136, 136)
        .setDepth(56);

      // -- Zone 2: name box --
      const nameBox = this.scene.add
        .rectangle(cx, topY + 30, itemW - 14, 17, 0x1e3050, 0.92)
        .setDepth(52);

      const elem = this.scene.add
        .text(cx, topY + 30, this.monsters[i].name.toUpperCase(), {
          fontFamily: "system-ui, Arial",
          fontSize: "11px",
          color: "#f1f5f9",
          fontStyle: "bold",
        })
        .setOrigin(0.5)
        .setDepth(53);

      // -- Zone 3: skill energy bar --
      const barBg = this.scene.add
        .rectangle(cx, topY + 44, itemW - 18, 13, 0x1f2937, 1)
        .setDepth(52);

      const barFill = this.scene.add
        .rectangle(cx - (itemW - 18) / 2, topY + 44, 1, 13, 0x22c55e, 1)
        .setOrigin(0, 0.5)
        .setDepth(53);

      // percentage overlaid on the bar
      const pct = this.scene.add
        .text(cx, topY + 44, "0%", {
          fontFamily: "system-ui, Arial",
          fontSize: "9px",
          color: "#94a3b8",
        })
        .setOrigin(0.5)
        .setDepth(54);

      // -- Badge de nível (canto inferior esquerdo do card, acima da barra XP) --
      const cardLeft = cx - (itemW - 10) / 2 + 4;
      const lvBadge = this.scene.add
        .text(cardLeft, topY + 17, "Lv1", {
          fontFamily: "system-ui, Arial",
          fontSize: "10px",
          color: "#64748b",
          fontStyle: "bold",
        })
        .setOrigin(0, 1)
        .setDepth(57);

      // -- Barra de XP / nível (fundo do card) --
      const xpBarW = itemW - 18;
      const xpBarBg = this.scene.add
        .rectangle(cx, topY + 53, xpBarW, 5, 0x1e293b, 1)
        .setDepth(52);
      const xpBarFill = this.scene.add
        .rectangle(cx - xpBarW / 2, topY + 53, 1, 5, 0x6366f1, 1)
        .setOrigin(0, 0.5)
        .setDepth(53);

      // -- No elemental ambience (removed) --
      const amb = { glow: null, emitter: null };

      // ✅ clique no card inteiro:
      // - se READY -> usa a skill
      // - senão -> só seleciona
      card.on("pointerdown", () => {
        const m = this.monsters[i];
        if (m.ready) {
          this.onUse(m);
          return;
        }
        this.setActive(i);
      });

      const elemColor = ELEM_BAR_COLOR[this.monsters[i].element] || ELEM_BAR_COLOR.fire;
      this.items.push({ card, icon, nameBox, elem, barBg, barFill, pct, lvBadge, xpBarBg, xpBarFill, itemW, elemColor, glow: amb.glow, particles: amb.emitter });
    }

    this.refresh();
  }

  setActive(i) {
    this.activeIndex = i;
    this.refresh();
  }

  getActiveMonster() {
    return this.monsters[this.activeIndex] || null;
  }

  isActiveReady() {
    const m = this.getActiveMonster();
    return !!m?.ready;
  }

  // energia simples (vai para o monstro ativo)
  addEnergy(amount) {
    const m = this.monsters[this.activeIndex];
    if (!m) return;
    m.energy = Math.min(m.energyRequired, m.energy + amount);
    m.ready = m.energy >= m.energyRequired;
    this.refresh();
  }

  // energia por cor
  // red->fogo, yellow->raio, blue->jato, green->terra, purple->controlo
  addEnergyByColor(gainByColor) {
    const map = {
      red: "fogo",
      yellow: "raio",
      blue: "jato",
      green: "terra",
      purple: "controlo",
    };

    for (const [color, gain] of Object.entries(gainByColor || {})) {
      if (!gain) continue;

      const monsterId = map[color];
      const m = this.monsters.find((x) => x.id === monsterId);
      if (!m) continue;

      m.energy = Math.min(m.energyRequired, m.energy + gain);
      m.ready = m.energy >= m.energyRequired;
    }

    this.refresh();
  }

  consumeEnergyById(monsterId) {
    const m = this.monsters.find((x) => x.id === monsterId);
    if (!m) return;
    m.energy = 0;
    m.ready = false;
    this.refresh();
  }

  // penalização (ex: -50% da barra)
  penalizeEnergyById(monsterId, fraction = 0.5) {
    const m = this.monsters.find((x) => x.id === monsterId);
    if (!m) return;

    const drop = Math.max(0, Math.floor(m.energyRequired * fraction));
    m.energy = Math.max(0, m.energy - drop);
    m.ready = m.energy >= m.energyRequired;

    this.refresh();
  }

  consumeActiveEnergy() {
    const m = this.monsters[this.activeIndex];
    if (!m) return;
    this.consumeEnergyById(m.id);
  }

  // ── XP dos monstros — preview durante o nível ─────────────────────────────
  // previewXp: XP guardado + ganhos desta sessão (antes de aplicar ao save)
  // level: nível actual do monstro (do localStorage)
  setMonsterXP(id, previewXp, level) {
    this._xpData[id] = { xp: previewXp, level };
    const idx = this.monsters.findIndex(m => m.id === id);
    if (idx >= 0) this._refreshXpBar(idx);
  }

  // ── Elemental ambience helpers ─────────────────────────────────────────────

  _ensureParticleTexture() {
    if (this.scene.textures.exists('_mb_dot')) return;
    const g = this.scene.make.graphics({ add: false });
    g.fillStyle(0xffffff, 1);
    g.fillCircle(4, 4, 4);
    g.generateTexture('_mb_dot', 8, 8);
    g.destroy();
  }

  _createElementAmbience() {
    // Elemental bubble/glow effect removed per design decision
    return { glow: null, emitter: null };
  }

  _showReadyQuip(idx) {
    const ui   = this.items[idx];
    const m    = this.monsters[idx];
    const pool = READY_QUIPS[m.id];
    if (!pool?.length) return;

    // Clear any previous quip for this slot
    if (ui.__quipBg)  { ui.__quipBg.destroy();  ui.__quipBg  = null; }
    if (ui.__quipTxt) { ui.__quipTxt.destroy();  ui.__quipTxt = null; }

    const phrase    = pool[Math.floor(Math.random() * pool.length)];
    const elemColor = ui.elemColor.charge;
    const cx        = ui.icon.x;
    const startY    = ui.icon.y - 86;

    const txt = this.scene.add.text(cx, startY, phrase, {
      fontFamily: 'system-ui, Arial',
      fontSize:   '10px',
      color:      '#f1f5f9',
      align:      'center',
      wordWrap:   { width: 112 },
    }).setOrigin(0.5).setAlpha(0).setDepth(68);

    const PAD_X = 10, PAD_Y = 6;
    const bw = Math.max(txt.width + PAD_X * 2, 60);
    const bh = txt.height + PAD_Y * 2;

    const bg = this.scene.add
      .rectangle(cx, startY, bw, bh, 0x0d1117, 0.92)
      .setStrokeStyle(1, elemColor, 1)
      .setAlpha(0).setDepth(67);

    ui.__quipBg  = bg;
    ui.__quipTxt = txt;

    // Fade in
    this.scene.tweens.add({ targets: [bg, txt], alpha: 1, duration: 200, ease: 'Quad.Out' });

    // Float upward
    this.scene.tweens.add({ targets: [bg, txt], y: `-=18`, duration: 2600, ease: 'Sine.Out' });

    // Fade out after hold
    this.scene.time.delayedCall(2200, () => {
      if (!bg.active) return;
      this.scene.tweens.add({
        targets: [bg, txt],
        alpha:   0,
        duration: 380,
        onComplete: () => {
          bg.destroy();  txt.destroy();
          if (ui.__quipBg  === bg)  ui.__quipBg  = null;
          if (ui.__quipTxt === txt) ui.__quipTxt = null;
        },
      });
    });

    // 🔊 Voice hook — silent fail until audio clips are loaded
    try { this.scene.sound.play(`voice-${m.id}-ready`, { volume: 0.85 }); } catch (_) {}
  }

  _refreshXpBar(idx) {
    const ui = this.items[idx];
    if (!ui?.xpBarFill || !ui?.lvBadge) return;

    const id   = this.monsters[idx].id;
    const data = this._xpData[id] || { xp: 0, level: 1 };
    const xp   = data.xp;
    const lv   = data.level;

    const T2 = 800, T3 = 3000; // thresholds (espelham levelStore.js)
    const maxW = ui.itemW - 18;

    let ratio = 0, barColor = 0x6366f1;

    if (lv >= 3) {
      ratio = 1; barColor = 0xf59e0b;          // dourado — max level
    } else if (lv === 2) {
      ratio = Math.min(1, (xp - T2) / (T3 - T2));
      barColor = 0x22d3ee;                       // ciano — Lv2→Lv3
    } else {
      ratio = Math.min(1, xp / T2);
      barColor = 0x6366f1;                       // índigo — Lv1→Lv2
    }

    ui.xpBarFill.width = Math.max(1, Math.floor(maxW * ratio));
    ui.xpBarFill.setFillStyle(barColor, 1);

    // badge de nível
    const badgeColors = { 1: "#64748b", 2: "#22c55e", 3: "#f59e0b" };
    ui.lvBadge.setText(`Lv${lv}`).setColor(badgeColors[lv] || "#64748b");
  }

  refresh() {
    for (let i = 0; i < this.items.length; i++) {
      const ui = this.items[i];
      const m = this.monsters[i];

      // READY → stroke verde (mantém)
      if (m.ready) {
        ui.card.setStrokeStyle(3, 0x22c55e, 1);
      }
      // ativo → branco
      else if (i === this.activeIndex) {
        ui.card.setStrokeStyle(3, 0xffffff, 0.9);
      }
      // normal
      else {
        ui.card.setStrokeStyle(2, 0x334155, 1);
      }

      const maxW = ui.itemW - 18;
      const ratio = m.energyRequired > 0 ? m.energy / m.energyRequired : 0;

      ui.barFill.width = Math.max(1, Math.floor(maxW * ratio));

      // ── Skill bar colour: element colour while charging, gold when ready ──
      if (ratio >= 1) {
        ui.barFill.setFillStyle(READY_COLOR, 1);
        ui.barFill.setScale(Theme.monsterBar.readyScale);
        if (!ui.barFill.__pulseTween) {
          ui.barFill.__pulseTween = this.scene.tweens.add({
            targets:  ui.barFill,
            alpha:    { from: 1, to: Theme.monsterBar.pulseAlphaMin },
            duration: Theme.monsterBar.pulseDuration,
            yoyo:     true,
            repeat:   -1,
          });
        }

        // ── Portrait ready pulse + glow aura (transition: not ready → ready) ──
        if (!ui.__wasReady) {
          ui.__wasReady = true;
          const icon = ui.icon;
          const bsx  = icon._baseSX ?? icon.scaleX;
          const bsy  = icon._baseSY ?? icon.scaleY;

          // Subtle scale pulse — no tint, keeps sprite colours intact
          ui.__iconReadyTween = this.scene.tweens.add({
            targets:  icon,
            scaleX:   { from: bsx, to: bsx * 1.05 },
            scaleY:   { from: bsy, to: bsy * 1.05 },
            duration: 550,
            yoyo:     true,
            repeat:   -1,
            ease:     'Sine.InOut',
          });

          // Subtle gold ring behind portrait
          const gfx = this.scene.add.graphics().setDepth(55);
          gfx.fillStyle(0xfcd34d, 0.12);
          gfx.fillCircle(icon.x, icon.y, 52);
          ui.__readyGlow = gfx;
          this.scene.tweens.add({
            targets:  gfx,
            alpha:    { from: 0.9, to: 0.25 },
            duration: 600,
            yoyo:     true,
            repeat:   -1,
            ease:     'Sine.InOut',
          });

          // Personality quip bubble
          this._showReadyQuip(i);
        }
      } else {
        ui.barFill.setFillStyle(ui.elemColor.charge, 1);
        ui.barFill.setScale(1);
        if (ui.barFill.__pulseTween) {
          ui.barFill.__pulseTween.stop();
          ui.barFill.__pulseTween = null;
          ui.barFill.alpha = 1;
        }

        // ── Clear portrait ready state ────────────────────────────────────
        if (ui.__wasReady) {
          ui.__wasReady = false;
          const icon = ui.icon;
          if (ui.__iconReadyTween) { ui.__iconReadyTween.stop(); ui.__iconReadyTween = null; }
          if (ui.__readyGlow)      { ui.__readyGlow.destroy();   ui.__readyGlow = null; }
          const bsx = icon._baseSX ?? icon.scaleX;
          const bsy = icon._baseSY ?? icon.scaleY;
          icon.setScale(bsx, bsy);
          // Dismiss quip early if still visible
          if (ui.__quipBg)  { ui.__quipBg.destroy();  ui.__quipBg  = null; }
          if (ui.__quipTxt) { ui.__quipTxt.destroy();  ui.__quipTxt = null; }
        }
      }

      // percentagem
      const pct = Math.round(Math.min(1, Math.max(0, ratio)) * 100);
      ui.pct.setText(`${pct}%`);
      ui.pct.setColor(m.ready ? "#1c1917" : "#ffffff");
    }
  }
}